import type { CogniteClient } from '@cognite/sdk';
import { NavigationProgress, nprogress } from '@mantine/nprogress';
import { type QueryClient, useIsFetching } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  retainSearchParams,
  useRouterState,
} from '@tanstack/react-router';
import { use, useEffect } from 'react';

import { ProjectContext } from '#core/contexts/ProjectContext.ts';

import classes from './__root.module.css';
import 'dayjs/locale/en';

import { zodValidator } from '@tanstack/zod-adapter';
import z from 'zod';

import { AppRoot } from '#core/components/layout/AppRoot.tsx';
import { Devtools } from '#core/components/layout/Devtools.tsx';
import { RootErrorBoundary } from '#core/components/layout/RootErrorBoundary.tsx';
import { Sidebar } from '#core/components/layout/Sidebar.tsx';
import { env } from '#core/lib/utils/env.ts';

import { dateRangeSearchSchema } from '../schema/searchParams.ts';

export interface RouteContext {
  getTitle?: () => string;
  queryClient: QueryClient;
  cogniteClient: CogniteClient;
}

export const Route = createRootRouteWithContext<RouteContext>()({
  component: RouteComponent,
  validateSearch: zodValidator(
    z.object({
      prompts: z.string().array().optional(),
      step: z.number().optional().catch(0),
      ...dateRangeSearchSchema.shape,
    }),
  ),
  search: {
    middlewares: [retainSearchParams(['prompts', 'step', 'startDate', 'endDate'])],
  },
  head: () => ({
    meta: [],
    links: [],
    styles: [],
    scripts: [
      // {
      //   src: 'https://cdn.jsdelivr.net/pyodide/v0.29.0/full/pyodide.js',
      // },
    ],
  }),
});

function RouteComponent() {
  const { config } = use(ProjectContext);
  const matches = useRouterState({ select: (state) => state.matches });

  const breadcrumbs = matches
    .filter((match) => match.context?.getTitle || match.staticData?.route?.title)
    .map(({ pathname, context, staticData }) => ({
      title: context?.getTitle?.() ?? staticData?.route?.title,
      path: pathname,
    }));

  const pageTitle = [`${[...new Set(breadcrumbs.toReversed().map(({ title }) => title))].join(' / ')}`, env.appName]
    .filter(Boolean)
    .join(' – ');

  // Drive the top navigation progress bar off the number of in-flight queries:
  // 0 ⇒ complete, 1 ⇒ start, more ⇒ creep toward done as the backlog drains.
  const isFetching = useIsFetching();
  useEffect(() => {
    if (isFetching === 0) {
      nprogress.complete();
      return;
    }
    if (isFetching === 1) {
      nprogress.start();
      return;
    }
    nprogress.set(Math.max(0, Math.min(100, (1 / isFetching) * 100)));
  }, [isFetching]);

  return (
    <>
      <HeadContent />
      <AppRoot>
        <title>{pageTitle}</title>
        {config?.faviconUrl !== undefined && <link href={config.faviconUrl} rel="icon" />}

        <NavigationProgress color="dark.3" />

        <Sidebar />

        <main className={classes.content}>
          <RootErrorBoundary>
            <Outlet />
          </RootErrorBoundary>
        </main>

        <Devtools />
      </AppRoot>
    </>
  );
}
